// src/components/EmailEditorParts.jsx
// Pieces shared by Control Panel → Email Templates (editing the system's
// emails) and Announcements (writing new ones): the rich-text box with
// {placeholder} chips, the placeholder list, the email preview, and the
// authenticated fetch.
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../firebase/config';
import { timeZone } from '../utils/timeZone';
import Emo from './Emo';

// fetch() with the sign-in token; throws the server's message
export async function apiCall(path, getToken, options = {}) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Something went wrong.');
  return json;
}

export const fmtWhen = iso => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export function Preview({ subject, html, title, height = 620 }) {
  return (
    <div style={P.previewBox}>
      <div style={P.subject}><span style={P.subjectLabel}>Subject</span> {subject}</div>
      <iframe title={title} srcDoc={html} sandbox="" style={{ ...P.frame, height }} />
    </div>
  );
}

// Where a clicked placeholder goes: the text field or rich-text box last
// focused, or the end of the body. Spread inputProps(name) on inputs and
// richProps on RichText boxes; registerBody on the body's RichText.
export function usePlaceholderTarget(draft, setField) {
  const focusRef = useRef(null);
  const bodyInsert = useRef(null);
  const insert = key => {
    const f = focusRef.current;
    if (f?.kind === 'input') {
      const v = draft[f.name] || '';
      const a = f.el.selectionStart ?? v.length, b = f.el.selectionEnd ?? v.length;
      setField(f.name, v.slice(0, a) + `{${key}}` + v.slice(b));
      requestAnimationFrame(() => { f.el.focus(); f.el.setSelectionRange(a + key.length + 2, a + key.length + 2); });
    } else if (f?.kind === 'rich') f.insert(key);
    else bodyInsert.current?.(key);
  };
  return {
    insert,
    inputProps: name => ({ onFocus: e => { focusRef.current = { kind: 'input', name, el: e.target }; } }),
    richProps: { onFocusField: f => { focusRef.current = f; } },
    registerBody: fn => { bodyInsert.current = fn; },
  };
}

export function PlaceholderPicker({ placeholders, onPick, note }) {
  if (!placeholders?.length) return null;
  return (
    <div style={P.phBox}>
      <div style={P.phTitle}>Placeholders <span style={P.phHelp}>Click one to add it where the cursor is. It's filled in for each person when the email is sent.</span></div>
      <div style={P.phList}>
        {placeholders.map(p => (
          <button key={p.key} type="button" onMouseDown={e => e.preventDefault()} onClick={() => onPick(p.key)}
            title={p.label} style={{ ...P.phChip, ...(p.block ? P.phChipBlock : {}) }}>
            {`{${p.key}}`}{p.required ? ' *' : ''}
            <span style={P.phLabel}>{p.label}</span>
          </button>
        ))}
      </div>
      {note && <p style={P.hint}>{note}</p>}
    </div>
  );
}

// ── Rich-text box: placeholders show as chips ───────────────
const PH = /\{([a-z_]+)\}/g;
function chip(doc, key, phMap) {
  const el = doc.createElement('span');
  el.setAttribute('data-ph', key);
  el.setAttribute('contenteditable', 'false');
  el.className = phMap[key] ? (phMap[key].block ? 'et-ph et-ph-block' : 'et-ph') : 'et-ph et-ph-bad';
  el.title = phMap[key]?.label || 'Unknown placeholder';
  el.textContent = `{${key}}`;
  return el;
}
function toEditor(html, phMap) {
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const root = doc.body.firstChild;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  for (const t of texts) {
    const s = t.nodeValue;
    if (s.search(/\{[a-z_]+\}/) < 0) continue;
    const frag = doc.createDocumentFragment();
    let last = 0;
    for (const m of s.matchAll(PH)) {
      if (m.index > last) frag.appendChild(doc.createTextNode(s.slice(last, m.index)));
      frag.appendChild(chip(doc, m[1], phMap));
      last = m.index + m[0].length;
    }
    if (last < s.length) frag.appendChild(doc.createTextNode(s.slice(last)));
    t.parentNode.replaceChild(frag, t);
  }
  return root.innerHTML;
}
function fromEditor(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  doc.querySelectorAll('span[data-ph]').forEach(el => el.replaceWith(doc.createTextNode(`{${el.getAttribute('data-ph')}}`)));
  return doc.body.firstChild.innerHTML;
}

// placeholders={false}: {words} stay plain text, no chips (Knowledge Base).
// headings: adds Heading / Subheading / Normal buttons.
// 3.54.0 (owner): pictures for emails. Only pictures on our own site can be
// used (lib/emailTemplates.js drops any other), so the Picture button offers
// the ones there, or takes the address of another picture on the site. New
// pictures are added to the site (public/) with a portal update.
const SITE = 'https://fax.crazypixels.org/portal';
const OUR_PICTURE = /^https:\/\/fax\.crazypixels\.org\/portal\/[^\s"'<>]+\.(png|jpe?g|gif|webp)$/i;
export const SITE_PICTURES = [
  { path: '/whats-new/img/hero.jpg', label: 'What’s new: the portal', alt: 'The Fax Portal Overview' },
  { path: '/whats-new/img/phone-app.jpg', label: 'The phone app', alt: 'The Fax Portal app on a phone' },
  { path: '/whats-new/img/unread.jpg', label: 'Unread faxes', alt: 'Unread faxes in the Inbox' },
  { path: '/whats-new/img/send.jpg', label: 'Sending a fax', alt: 'Sending a fax with Compose' },
  { path: '/whats-new/img/overview.jpg', label: 'The Overview', alt: 'The Overview page' },
  { path: '/whats-new/img/fax-by-email.jpg', label: 'Faxing by email', alt: 'Faxing by email' },
  { path: '/whats-new/img/library.jpg', label: 'The Library', alt: 'The Library and training' },
  { path: '/whats-new/img/admins.jpg', label: 'For Admins', alt: 'Settings for Admins' },
  { path: '/brand/crazy-pixels.png', label: 'Crazy Pixels logo', alt: 'Crazy Pixels', logo: true },
  { path: '/brand/crazy-pixels-fax-portal.png', label: 'Fax Portal logo', alt: 'Crazy Pixels Fax Portal', logo: true },
];
const PICTURE_SIZES = [['full', 'Full width', 560], ['half', 'Half', 280], ['small', 'Small', 160]];
export function pictureHtml(src, alt, size = 'full', logo = false) {
  const w = (PICTURE_SIZES.find(x => x[0] === size) || PICTURE_SIZES[0])[2];
  const esc = v => String(v || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  return `<img src="${esc(src)}" alt="${esc(alt)}" width="${w}" style="width:100%;max-width:${w}px;height:auto;display:block;margin:12px 0;border:0${logo ? '' : ';border-radius:10px'}">`;
}

// 3.67.0: links={false} hides Link/Unlink (the cover sheet message);
// cleanPaste(html) → what a paste may keep (else pasted as it comes)
export function RichText({ initial, phMap = {}, onChange, onFocusField, registerInsert, labelId, minHeight, maxHeight, placeholders = true, headings = false, pictures = true, links = true, cleanPaste }) {
  const ref = useRef(null);
  const rangeRef = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState('https://');
  const [picOpen, setPicOpen] = useState(false);
  const [pic, setPic] = useState({ src: '', alt: '', size: 'full', logo: false });

  // Filled once; the parent remounts it (key) to load other text
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ref.current.innerHTML = placeholders ? toEditor(initial, phMap) : (initial || ''); }, []);
  useEffect(() => {
    const onSel = () => {
      const sel = document.getSelection();
      if (sel.rangeCount && ref.current?.contains(sel.anchorNode)) rangeRef.current = sel.getRangeAt(0).cloneRange();
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);

  const emit = useCallback(() => onChange(placeholders ? fromEditor(ref.current.innerHTML) : ref.current.innerHTML), [onChange, placeholders]);
  const restore = () => {
    const el = ref.current;
    el.focus();
    let range = rangeRef.current;
    if (!range || !el.contains(range.startContainer)) { range = document.createRange(); range.selectNodeContents(el); range.collapse(false); }
    const sel = document.getSelection();
    sel.removeAllRanges(); sel.addRange(range);
    return range;
  };
  const insert = useCallback(key => {
    const range = restore();
    range.deleteContents();
    const node = chip(document, key, phMap);
    range.insertNode(node);
    range.setStartAfter(node); range.collapse(true);
    const sel = document.getSelection();
    sel.removeAllRanges(); sel.addRange(range);
    emit();
  }, [phMap, emit]);
  useEffect(() => { registerInsert?.(insert); }, [registerInsert, insert]);

  const exec = (cmd, arg) => { restore(); document.execCommand(cmd, false, arg); emit(); };
  const addLink = () => {
    const u = url.trim();
    if (!/^(https?:\/\/[^\s]+|mailto:[^\s]+)$/i.test(u)) return;
    exec('createLink', u);
    setLinkOpen(false); setUrl('https://');
  };
  const addPicture = () => {
    const src = pic.src.trim();
    if (!OUR_PICTURE.test(src)) return;
    exec('insertHTML', pictureHtml(src, pic.alt.trim(), pic.size, pic.logo));
    setPicOpen(false); setPic({ src: '', alt: '', size: 'full', logo: false });
  };
  const picOk = OUR_PICTURE.test(pic.src.trim());
  const tools = [
    ['bold', <b key="b">B</b>, 'Bold'], ['italic', <i key="i">I</i>, 'Italic'], ['underline', <u key="u">U</u>, 'Underline'],
    ['insertUnorderedList', '• List', 'Bulleted list'], ['insertOrderedList', '1. List', 'Numbered list'],
  ];
  const blocks = headings ? [['<h3>', 'Heading', 'Make this line a heading'], ['<h4>', 'Subheading', 'Make this line a subheading'], ['<p>', 'Normal', 'Make this line normal text']] : [];

  return (
    <div style={P.richWrap}>
      <div style={P.toolbar} role="toolbar" aria-label="Formatting">
        {tools.map(([cmd, icon, label]) => (
          <button key={cmd} type="button" title={label} aria-label={label} onMouseDown={e => { e.preventDefault(); exec(cmd); }} style={P.tool}>{icon}</button>
        ))}
        {blocks.map(([tag, text, label]) => (
          <button key={tag} type="button" title={label} aria-label={label} onMouseDown={e => { e.preventDefault(); exec('formatBlock', tag); }} style={P.tool}>{text}</button>
        ))}
        {links && <button type="button" title="Link" aria-label="Add a link" onMouseDown={e => e.preventDefault()} onClick={() => setLinkOpen(o => !o)} style={P.tool}><Emo e="🔗" /> Link</button>}
        {pictures && <button type="button" title="Picture" aria-label="Add a picture" aria-expanded={picOpen} onMouseDown={e => e.preventDefault()} onClick={() => { setPicOpen(o => !o); setLinkOpen(false); }} style={P.tool}><Emo e="🖼️" /> Picture</button>}
        {links && <button type="button" title="Remove link" aria-label="Remove link" onMouseDown={e => { e.preventDefault(); exec('unlink'); }} style={P.tool}>Unlink</button>}
        <button type="button" title="Clear formatting" aria-label="Clear formatting" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }} style={P.tool}>Clear</button>
      </div>
      {linkOpen && (
        <div style={P.linkRow}>
          <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
            aria-label="Link address" style={{ ...P.input, margin: 0, flex: 1 }} />
          <button type="button" onClick={addLink} style={P.smallBtn}>Add link to the selected text</button>
        </div>
      )}
      {picOpen && (
        <div style={P.picPanel}>
          <div style={P.picHelp}>Pictures can only come from our own site (fax.crazypixels.org/portal). Pick one, or paste the address of another picture there.</div>
          <div style={P.picGrid} role="listbox" aria-label="Pictures on our site">
            {SITE_PICTURES.map(x => {
              const on = pic.src === SITE + x.path;
              return (
                <button key={x.path} type="button" role="option" aria-selected={on} onMouseDown={e => e.preventDefault()}
                  onClick={() => setPic(p => ({ ...p, src: SITE + x.path, alt: p.alt && !SITE_PICTURES.some(y => y.alt === p.alt) ? p.alt : x.alt, logo: !!x.logo, size: x.logo ? 'half' : p.size }))}
                  style={{ ...P.picTile, ...(on ? P.picTileOn : {}) }} title={x.label}>
                  <img src={`${process.env.PUBLIC_URL}${x.path}`} alt="" style={P.picThumb} />
                  <span style={P.picLabel}>{x.label}</span>
                </button>
              );
            })}
          </div>
          <label style={P.picField}>Picture address
            <input value={pic.src} onChange={e => setPic(p => ({ ...p, src: e.target.value }))} placeholder={`${SITE}/whats-new/img/hero.jpg`} style={{ ...P.input, margin: 0 }} />
          </label>
          {pic.src.trim() && !picOk && <div role="alert" style={P.picErr}>Only pictures on fax.crazypixels.org/portal/ (ending .png, .jpg, .gif or .webp) can be used in emails.</div>}
          <label style={P.picField}>Describe it in a few words <span style={P.picHint}>(read aloud by screen readers, and shown if pictures are blocked)</span>
            <input value={pic.alt} onChange={e => setPic(p => ({ ...p, alt: e.target.value }))} maxLength={120} style={{ ...P.input, margin: 0 }} />
          </label>
          <div style={P.picRow} role="radiogroup" aria-label="Size">
            {PICTURE_SIZES.map(([k, label]) => (
              <button key={k} type="button" role="radio" aria-checked={pic.size === k} onMouseDown={e => e.preventDefault()} onClick={() => setPic(p => ({ ...p, size: k }))}
                style={{ ...P.picSize, ...(pic.size === k ? P.picSizeOn : {}) }}>{label}</button>
            ))}
            <span style={{ flex: 1 }} />
            <button type="button" onClick={addPicture} disabled={!picOk} style={P.smallBtn}>Add the picture where the cursor is</button>
          </div>
        </div>
      )}
      <div ref={ref} className="et-rich" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-labelledby={labelId}
        onInput={emit} onFocus={() => onFocusField?.({ kind: 'rich', insert })}
        onPaste={cleanPaste ? e => {
          const html = e.clipboardData.getData('text/html'), text = e.clipboardData.getData('text/plain');
          e.preventDefault();
          if (html) document.execCommand('insertHTML', false, cleanPaste(html)); else document.execCommand('insertText', false, text);
          emit();
        } : undefined}
        style={{ ...P.rich, minHeight, ...(maxHeight ? { maxHeight, overflowY: 'auto', resize: 'vertical' } : {}) }} />
    </div>
  );
}

export const EDITOR_CSS = `
.et-rich p{margin:0 0 10px}
.et-rich ol,.et-rich ul{margin:0 0 10px;padding-left:22px}
.et-rich h3{margin:14px 0 8px;font-size:18px;color:var(--t-00695c)}
.et-rich h4{margin:12px 0 6px;font-size:15px;color:var(--t-263238)}
.et-rich img{max-width:100%;height:auto}
.et-rich .info-box{background:var(--c-f5f7f8);border-left:4px solid var(--c-00897b);padding:10px 14px;border-radius:4px;margin:10px 0}
.et-rich .status-badge{display:inline-block;padding:1px 8px;border-radius:10px;background:var(--c-e0f2f1);font-size:12px}
.et-rich .note{font-size:13px;color:var(--t-78909c)}
.et-rich table{border-collapse:collapse}
.et-rich td,.et-rich th{border:1px dashed var(--c-cfd8dc);padding:3px 6px}
.et-rich a{color:var(--t-00897b)}
.et-ph{display:inline-block;padding:0 7px;margin:0 1px;border-radius:10px;background:var(--c-e0f2f1);color:var(--t-00695c);font-size:12px;font-weight:600;font-family:ui-monospace,Menlo,monospace;white-space:nowrap;cursor:default}
.et-ph-block{background:var(--c-ede7f6);color:var(--t-4527a0)}
.et-ph-bad{background:var(--c-fce4ec);color:var(--t-b71c1c)}
`;


const P = {
  previewBox: { border: '1px solid var(--c-eceff1)', borderRadius: 10, overflow: 'hidden' },
  subject: { padding: '9px 12px', background: 'var(--c-f9fafb)', borderBottom: '1px solid var(--c-eceff1)', fontSize: 13, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  subjectLabel: { fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', marginRight: 6 },
  frame: { width: '100%', border: 'none', background: 'var(--c-f4f6f8)', display: 'block' },
  hint: { margin: '6px 0 0', color: 'var(--t-90a4ae)', fontSize: 12, lineHeight: 1.45 },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit', marginBottom: 2 },
  richWrap: { border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, overflow: 'hidden', background: 'var(--c-ffffff)' },
  toolbar: { display: 'flex', gap: 4, flexWrap: 'wrap', padding: 6, background: 'var(--c-f9fafb)', borderBottom: '1px solid var(--c-eceff1)' },
  tool: { minWidth: 30, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  linkRow: { display: 'flex', gap: 6, padding: 6, borderBottom: '1px solid var(--c-eceff1)', flexWrap: 'wrap' },
  // 3.54.0: the Picture panel
  picPanel: { display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderBottom: '1px solid var(--c-eceff1)' },
  picHelp: { fontSize: 12.5, color: 'var(--t-607d8b)' },
  picGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 },
  picTile: { display: 'flex', flexDirection: 'column', gap: 4, padding: 4, borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
  picTileOn: { borderColor: 'var(--c-00897b)', boxShadow: '0 0 0 2px rgb(var(--c-00897b-rgb) / 0.25)' },
  picThumb: { width: '100%', height: 62, objectFit: 'contain', background: 'var(--c-f5f7f8)', borderRadius: 5 },
  picLabel: { fontSize: 11.5, color: 'var(--t-37474f)', lineHeight: 1.25 },
  picField: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 600, color: 'var(--t-37474f)' },
  picHint: { fontWeight: 400, color: 'var(--t-90a4ae)' },
  picErr: { fontSize: 12.5, color: 'var(--t-c62828)' },
  picRow: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' },
  picSize: { padding: '5px 10px', borderRadius: 999, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  picSizeOn: { borderColor: 'var(--c-00897b)', background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)' },
  smallBtn: { padding: '6px 10px', borderRadius: 6, border: 'none', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  rich: { padding: '10px 12px', fontSize: 14, lineHeight: 1.55, color: 'var(--t-333333)', outline: 'none', overflowWrap: 'anywhere' },
  phBox: { marginTop: 14, background: 'var(--c-fafafa)', border: '1px solid var(--c-eceff1)', borderRadius: 10, padding: '10px 12px' },
  phTitle: { fontSize: 12, fontWeight: 700, color: 'var(--t-455a64)', marginBottom: 6 },
  phHelp: { fontWeight: 400, color: 'var(--t-90a4ae)', marginLeft: 6 },
  phList: { display: 'flex', flexDirection: 'column', gap: 4 },
  phChip: { display: 'flex', gap: 8, alignItems: 'baseline', textAlign: 'left', padding: '4px 8px', borderRadius: 8, border: '1px solid var(--c-b2dfdb)', background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace', cursor: 'pointer' },
  phChipBlock: { border: '1px solid var(--c-d1c4e9)', background: 'var(--c-ede7f6)', color: 'var(--t-4527a0)' },
  phLabel: { fontWeight: 400, color: 'var(--t-546e7a)', fontFamily: "'Segoe UI', Arial, sans-serif" },
};
